import { Button } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { downloadResponse } from 'probonio-shared-ui/module/api';
import { useMeStateSelector } from 'probonio-shared-ui/module/me/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useContractPdfAvailable } from '../hooks/useContractPdfAvailable';
import { BikeleasingContractDocumentType, CONTRACT_DOCUMENTS } from '../model/contract-documents';

export const ContractDocumentDownloadButton: React.FC<{ contractId: string; documentType: BikeleasingContractDocumentType }> = ({
  contractId,
  documentType,
}) => {
  const { t } = useTranslation('extensions');
  const tenantId = useMeStateSelector(state => state.me.currentTenantId);
  const { pdfAvailable, isCheckingAvailability, markUnavailable } = useContractPdfAvailable(contractId, documentType);
  const documentConfig = CONTRACT_DOCUMENTS[documentType];

  const { mutate: download, isPending } = useMutation({
    mutationFn: async (mutationContractId: string) => {
      if (!tenantId) throw new Error('Missing current tenant id');
      const response = await documentConfig.downloadFile({ tenantId, contractId: mutationContractId });
      downloadResponse(response, documentConfig.defaultFileName);
    },
    onError: (err, mutationContractId) => {
      if (mutationContractId !== contractId) {
        return;
      } else if (err instanceof AxiosError && err.response?.status === 404) {
        markUnavailable();
      } else {
        enqueueSnackbar({ variant: 'error', message: t(documentConfig.downloadError) });
      }
    },
  });

  const isUnavailable = pdfAvailable === false;

  return (
    <Button
      loading={isPending || isCheckingAvailability}
      onClick={() => download(contractId)}
      disabled={!tenantId || isUnavailable || isCheckingAvailability}
      sx={{ height: 'auto' }}
    >
      {t(isUnavailable ? documentConfig.notAvailable : documentConfig.label)}
    </Button>
  );
};
